import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronRight, Clock3, Crown, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBookingsSupabaseStore } from "@/store/api/bookingsSupabaseStore";
import { useUserStore } from "@/store/userStore";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";
import emsSuit from "@/assets/vb20-ems-suit.png";

export function NextSessionCard() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation("athlete");
  const { bookings } = useBookingsSupabaseStore();
  const userId = useUserStore((state) => state.user?.id);
  const { athleteTheme } = useThemeCustomization();
  const isVb20 = athleteTheme.variant === "dark-premium";

  const nextBooking = useMemo(() => bookings
    .filter((booking) => booking.userId === userId && booking.status === "upcoming")
    .sort((a, b) => new Date(`${a.date}T${a.time}`).getTime() - new Date(`${b.date}T${b.time}`).getTime())[0],
  [bookings, userId]);

  if (!nextBooking) {
    return (
      <section className={`athlete-feature-card athlete-next-session-card ${isMobile ? "p-2" : ""}`}>
        <div className={`flex items-center gap-2 font-semibold text-primary ${isMobile ? "text-xs" : "text-sm"}`}>
          <Crown className={isMobile ? "h-3.5 w-3.5" : "h-5 w-5"} />
          {t("dashboard.nextSession")}
        </div>
        <p className={`mt-1 text-sm text-muted-foreground ${isMobile ? "text-xs" : ""}`}>
          {t("dashboard.noSessionPlanned", { defaultValue: "Tu n'as pas encore de séance prévue" })}
        </p>
        <Button
          onClick={() => navigate("/athlete/schedule")}
          className={`justify-between font-semibold ${isMobile ? "mt-2 h-7 w-full text-sm" : "mt-3 w-full text-base"}`}
        >
          {t("dashboard.quickBook")}
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </section>
    );
  }

  const isToday = nextBooking.date === new Date().toISOString().split("T")[0];
  const dateLabel = isToday
    ? t("dashboard.todayLabel", { defaultValue: "Aujourd'hui" })
    : new Intl.DateTimeFormat(i18n.language, {
        weekday: "long",
        day: "numeric",
        month: "long",
      }).format(new Date(`${nextBooking.date}T${nextBooking.time || "00:00"}`));

  const iconSize = isMobile ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <section className={`athlete-feature-card athlete-next-session-card relative overflow-hidden ${isMobile ? "p-3" : "p-4"}`}>
      {isVb20 && (
        <img src={emsSuit} loading="lazy" width={768} height={1024} alt="" aria-hidden="true" className="athlete-next-session-suit" />
      )}
      <div className={isVb20 ? "relative z-10 max-w-[64%]" : "relative z-10"}>
        <div className={`flex items-center gap-2 font-semibold text-primary ${isMobile ? "text-xs" : "text-sm"}`}>
          <Crown className={isMobile ? "h-3.5 w-3.5" : "h-5 w-5"} />
          {t("dashboard.nextSession")}
        </div>
        <h2 className={`mt-0.5 font-extrabold leading-tight truncate ${isMobile ? "text-xl" : "text-2xl"}`}>
          {nextBooking.className}
        </h2>
        <div className={`space-y-0.5 text-foreground/90 ${isMobile ? "mt-1 text-xs" : "mt-2 text-sm"}`}>
        <p className="flex items-center gap-2">
          <CalendarDays className={iconSize} />
          {dateLabel} · {nextBooking.time}
          {isMobile && <span className="min-w-0">· {nextBooking.coach}</span>}
        </p>
        <p className="flex items-center gap-2">
          <Clock3 className={iconSize} />
          {nextBooking.duration} min
          {!isMobile && (
            <span className="flex min-w-0 items-center gap-2">
              <UserRound className={iconSize} />
              {nextBooking.coach}
            </span>
          )}
        </p>
        </div>
      </div>
      {isVb20 || !isMobile ? (
        <Button
          onClick={() => navigate("/athlete/reservations")}
          className="athlete-cta relative z-10 mt-2 w-full justify-between text-sm font-bold"
        >
          {t("dashboard.viewMySession", { defaultValue: "Voir ma séance" })}
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      ) : null}
    </section>
  );
}
